// Sistemas → Gestión Base de Datos → Respaldos (/seguridad/respaldos).
// Gestión de respaldos de la BD (script 316 + respaldos-worker/): repositorios
// local/SSH, programaciones cron con modo full (incremental = fase 2) y
// política de retención GFS o rotativa, historial verificado (SHA-256 +
// pg_restore --list) y "Respaldar ahora". Config GLOBAL del servidor (nivel 5).
import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, DatabaseBackup, Pencil, Play, Plus, Trash2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Campo, fieldCls, Switch } from '@/features/admin/shared';
import { SortTh, useSort } from '@/features/control-bancario/shared';
import { LOCALE } from '@/lib/utils/regional';

interface Repo {
  id: string; nombre: string; tipo: 'local' | 'ssh'; ruta: string;
  ssh_destino: string | null; activo: boolean;
}
interface Prog {
  id: string; nombre: string; modo: 'full' | 'incremental'; horario: string;
  politica: 'gfs' | 'rotativo'; ret_diarios: number; ret_semanales: number;
  ret_mensuales: number; ret_horas: number; activo: boolean;
}
interface RepoResultado { repo: string; tipo: string; estado: string; error: string | null; purgados?: number }
interface Ejecucion {
  id: string; nombre: string | null; modo: string; politica: string; estado: string;
  inicio: string; fin: string | null; archivo: string | null; tamano_bytes: number | null;
  sha256: string | null; verificacion: string | null; repos: RepoResultado[] | null; error: string | null;
}
interface Estado {
  worker: boolean;
  ultimo_ok: { fin: string; archivo: string; tamano_bytes: number; nombre: string } | null;
  repositorios: Repo[]; programaciones: Prog[]; ejecuciones: Ejecucion[];
}

const PRESETS = [
  { v: '0 1 * * *', l: 'Diario a la 1 AM' },
  { v: '0 */6 * * *', l: 'Cada 6 horas' },
  { v: '0 * * * *', l: 'Cada hora' },
  { v: '0 2 * * 0', l: 'Domingos 2 AM' },
  { v: '', l: 'Personalizado…' },
];

const REPO0 = { nombre: '', tipo: 'local' as Repo['tipo'], ruta: '', ssh_destino: '', activo: true };
const PROG0 = {
  nombre: '', modo: 'full' as Prog['modo'], horario: '0 1 * * *', politica: 'gfs' as Prog['politica'],
  ret_diarios: '7', ret_semanales: '4', ret_mensuales: '12', ret_horas: '48', activo: true,
};

const mb = (b: number | null) => (b == null ? '—' : `${(b / 1048576).toFixed(1)} MB`);
const haceHoras = (iso: string) => Math.round((Date.now() - new Date(iso).getTime()) / 3600000);
const durSeg = (e: Ejecucion) =>
  e.fin ? `${Math.max(1, Math.round((new Date(e.fin).getTime() - new Date(e.inicio).getTime()) / 1000))} s` : '—';

export function Respaldos() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const [formRepo, setFormRepo] = useState<typeof REPO0 | null>(null);
  const [repoId, setRepoId] = useState<string | null>(null);
  const [formProg, setFormProg] = useState<typeof PROG0 | null>(null);
  const [progId, setProgId] = useState<string | null>(null);

  const { data } = useQuery<Estado>({
    queryKey: ['sys-respaldos'],
    queryFn: async () => (await apiClient.post('/rpc/sys_respaldos_estado', {})).data,
    staleTime: 0, refetchOnMount: 'always',
    refetchInterval: (q) =>
      (q.state.data?.ejecuciones ?? []).some((e) => e.estado === 'pendiente' || e.estado === 'en_proceso') ? 5000 : 30000,
  });

  const invalidar = () => qc.invalidateQueries({ queryKey: ['sys-respaldos'] });
  const opciones = (ok: string) => ({
    onSuccess: () => { invalidar(); toast.success(ok); },
    onError: (e: unknown) => toast.error(getErrorMessage(e)),
  });
  const guardarRepo = useMutation({
    mutationFn: async (body: Record<string, unknown>) => (await apiClient.post('/rpc/sys_respaldo_repo_guardar', body)).data,
    ...opciones('Repositorio guardado'),
  });
  const guardarProg = useMutation({
    mutationFn: async (body: Record<string, unknown>) => (await apiClient.post('/rpc/sys_respaldo_prog_guardar', body)).data,
    ...opciones('Programación guardada'),
  });
  const respaldarAhora = useMutation({
    mutationFn: async (body: Record<string, unknown>) => (await apiClient.post('/rpc/sys_respaldo_ahora', body)).data,
    ...opciones('Respaldo encolado: el worker lo ejecuta enseguida'),
  });

  const ejecuciones = useMemo(() => data?.ejecuciones ?? [], [data]);
  const getVal = useCallback((e: Ejecucion, key: string): string | number | null => {
    switch (key) {
      case 'inicio': return e.inicio;
      case 'nombre': return e.nombre;
      case 'estado': return e.estado;
      case 'tamano': return e.tamano_bytes;
      case 'verificacion': return e.verificacion;
      default: return null;
    }
  }, []);
  const sort = useSort(ejecuciones, getVal);

  const ultimoHace = data?.ultimo_ok ? haceHoras(data.ultimo_ok.fin) : null;
  const alerta = !data ? null
    : !data.worker ? 'El worker de respaldos NO está conectado: nada se respaldará hasta que TI lo arranque (systemd).'
    : ultimoHace == null ? 'Aún no existe ningún respaldo exitoso.'
    : ultimoHace > 26 ? `El último respaldo exitoso fue hace ${ultimoHace} horas — revisa el historial.` : null;

  const abrirRepo = (r?: Repo) => {
    setRepoId(r?.id ?? null);
    setFormRepo(r ? { nombre: r.nombre, tipo: r.tipo, ruta: r.ruta, ssh_destino: r.ssh_destino ?? '', activo: r.activo } : { ...REPO0 });
  };
  const abrirProg = (p?: Prog) => {
    setProgId(p?.id ?? null);
    setFormProg(p ? {
      nombre: p.nombre, modo: p.modo, horario: p.horario, politica: p.politica,
      ret_diarios: String(p.ret_diarios), ret_semanales: String(p.ret_semanales),
      ret_mensuales: String(p.ret_mensuales), ret_horas: String(p.ret_horas), activo: p.activo,
    } : { ...PROG0 });
  };

  return (
    <div className="p-6 space-y-4 max-w-6xl">
      {dlg.ui}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <DatabaseBackup className="w-5 h-5" /> Respaldos de la Base de Datos
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${data?.worker
            ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
            : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'}`}>
            {data?.worker ? 'Worker conectado' : 'Worker desconectado'}
          </span>
        </h1>
        <Button size="sm" onClick={() => respaldarAhora.mutate({ _modo: 'full' })} isLoading={respaldarAhora.isPending}>
          <Play className="w-4 h-4 mr-1" /> Respaldar ahora
        </Button>
      </div>

      {alerta && (
        <div className="flex items-start gap-2 text-sm border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 rounded-md p-3">
          <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 shrink-0" />
          <span>{alerta}</span>
        </div>
      )}
      {data?.ultimo_ok && (
        <div className="text-sm text-muted-foreground">
          Último respaldo OK: <span className="font-medium text-foreground">{data.ultimo_ok.archivo}</span>{' '}
          ({mb(data.ultimo_ok.tamano_bytes)}) · hace {ultimoHace} h ·{' '}
          {new Date(data.ultimo_ok.fin).toLocaleString(LOCALE, { hour12: false })}
        </div>
      )}

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Repositorios (a dónde se copia cada respaldo)</CardTitle>
            <Button size="sm" variant="outline" onClick={() => abrirRepo()}><Plus className="w-4 h-4 mr-1" /> Nuevo repositorio</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-muted-foreground border-b">
                <th className="py-2 pr-3 text-right w-10">N°</th>
                <th className="py-2 pr-3 text-left">Nombre</th>
                <th className="py-2 pr-3 text-left">Tipo</th>
                <th className="py-2 pr-3 text-left">Ruta</th>
                <th className="py-2 pr-3 text-left">Destino SSH</th>
                <th className="py-2 pr-3 text-center">Activo</th>
                <th className="py-2 pr-3" />
              </tr>
            </thead>
            <tbody>
              {(data?.repositorios ?? []).map((r, i) => (
                <tr key={r.id} className="border-b last:border-0">
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3 font-medium">{r.nombre}</td>
                  <td className="py-2 pr-3 uppercase text-xs">{r.tipo}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{r.ruta}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{r.ssh_destino ?? '—'}</td>
                  <td className="py-2 pr-3 text-center">{r.activo ? '✓' : '—'}</td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" title="Editar" onClick={() => abrirRepo(r)}><Pencil className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" title="Eliminar" onClick={async () => {
                      if (await dlg.confirmar(`¿Eliminar el repositorio "${r.nombre}"?`, { detalle: 'Los archivos ya copiados no se borran.', peligro: true }))
                        guardarRepo.mutate({ _data: {}, _id: r.id, _eliminar: true });
                    }}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {formRepo && (
            <div className="border rounded-md p-3 space-y-3">
              <div className="text-sm font-medium">{repoId ? 'Editar repositorio' : 'Nuevo repositorio'}</div>
              <div className="grid gap-3 md:grid-cols-4">
                <Campo label="Nombre *">
                  <input className={fieldCls} value={formRepo.nombre}
                         onChange={(e) => setFormRepo({ ...formRepo, nombre: e.target.value })} />
                </Campo>
                <Campo label="Tipo">
                  <select className={fieldCls} value={formRepo.tipo}
                          onChange={(e) => setFormRepo({ ...formRepo, tipo: e.target.value as Repo['tipo'] })}>
                    <option value="local">Local (directorio del servidor)</option>
                    <option value="ssh">Remoto por SSH (rsync)</option>
                  </select>
                </Campo>
                <Campo label="Ruta (directorio destino) *">
                  <input className={fieldCls} placeholder="/opt/respaldos/erp" value={formRepo.ruta}
                         onChange={(e) => setFormRepo({ ...formRepo, ruta: e.target.value })} />
                </Campo>
                {formRepo.tipo === 'ssh' && (
                  <Campo label="Destino SSH (usuario@host) *">
                    <input className={fieldCls} placeholder="usuario@205.235.2.197" value={formRepo.ssh_destino}
                           onChange={(e) => setFormRepo({ ...formRepo, ssh_destino: e.target.value })} />
                  </Campo>
                )}
              </div>
              <div className="flex items-center justify-between">
                <Switch checked={formRepo.activo} onChange={(v) => setFormRepo({ ...formRepo, activo: v })} label="Activo (recibe cada respaldo)" />
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setFormRepo(null)}>Cancelar</Button>
                  <Button size="sm" isLoading={guardarRepo.isPending}
                          disabled={!formRepo.nombre.trim() || !formRepo.ruta.trim() || (formRepo.tipo === 'ssh' && !formRepo.ssh_destino.trim())}
                          onClick={() => guardarRepo.mutate({ _data: formRepo, _id: repoId }, { onSuccess: () => setFormRepo(null) })}>
                    Guardar
                  </Button>
                </div>
              </div>
              {formRepo.tipo === 'ssh' && (
                <p className="text-xs text-muted-foreground">
                  La autenticación es por LLAVE SSH del usuario que corre el worker (sin contraseñas). TI debe haber
                  intercambiado la llave con el servidor destino.
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Programaciones (cuándo y cómo se respalda)</CardTitle>
            <Button size="sm" variant="outline" onClick={() => abrirProg()}><Plus className="w-4 h-4 mr-1" /> Nueva programación</Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-muted-foreground border-b">
                <th className="py-2 pr-3 text-right w-10">N°</th>
                <th className="py-2 pr-3 text-left">Nombre</th>
                <th className="py-2 pr-3 text-left">Modo</th>
                <th className="py-2 pr-3 text-left">Horario (cron)</th>
                <th className="py-2 pr-3 text-left">Retención</th>
                <th className="py-2 pr-3 text-center">Activa</th>
                <th className="py-2 pr-3" />
              </tr>
            </thead>
            <tbody>
              {(data?.programaciones ?? []).map((p, i) => (
                <tr key={p.id} className="border-b last:border-0">
                  <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                  <td className="py-2 pr-3 font-medium">{p.nombre}</td>
                  <td className="py-2 pr-3 uppercase text-xs">{p.modo}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{p.horario}</td>
                  <td className="py-2 pr-3 text-xs">
                    {p.politica === 'gfs'
                      ? `GFS: ${p.ret_diarios} diarios · ${p.ret_semanales} semanales · ${p.ret_mensuales} mensuales`
                      : `Rotativa: se conservan ${p.ret_horas} horas`}
                  </td>
                  <td className="py-2 pr-3 text-center">{p.activo ? '✓' : '—'}</td>
                  <td className="py-2 pr-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" title="Editar" onClick={() => abrirProg(p)}><Pencil className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" title="Eliminar" onClick={async () => {
                      if (await dlg.confirmar(`¿Eliminar la programación "${p.nombre}"?`, { peligro: true }))
                        guardarProg.mutate({ _data: {}, _id: p.id, _eliminar: true });
                    }}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {formProg && (
            <div className="border rounded-md p-3 space-y-3">
              <div className="text-sm font-medium">{progId ? 'Editar programación' : 'Nueva programación'}</div>
              <div className="grid gap-3 md:grid-cols-4">
                <Campo label="Nombre *">
                  <input className={fieldCls} value={formProg.nombre}
                         onChange={(e) => setFormProg({ ...formProg, nombre: e.target.value })} />
                </Campo>
                <Campo label="Modo">
                  <select className={fieldCls} value={formProg.modo}
                          onChange={(e) => setFormProg({ ...formProg, modo: e.target.value as Prog['modo'] })}>
                    <option value="full">Full (pg_dump completo)</option>
                    <option value="incremental">Incremental (fase 2)</option>
                  </select>
                </Campo>
                <Campo label="Horario">
                  <select className={fieldCls}
                          value={PRESETS.some((x) => x.v === formProg.horario) ? formProg.horario : ''}
                          onChange={(e) => setFormProg({ ...formProg, horario: e.target.value || formProg.horario })}>
                    {PRESETS.map((x) => <option key={x.l} value={x.v}>{x.l}</option>)}
                  </select>
                </Campo>
                <Campo label="Cron (min hora díames mes díasem)">
                  <input className={`${fieldCls} font-mono`} value={formProg.horario}
                         onChange={(e) => setFormProg({ ...formProg, horario: e.target.value })} />
                </Campo>
                <Campo label="Política de retención">
                  <select className={fieldCls} value={formProg.politica}
                          onChange={(e) => setFormProg({ ...formProg, politica: e.target.value as Prog['politica'] })}>
                    <option value="gfs">GFS (diarios + semanales + mensuales)</option>
                    <option value="rotativo">Rotativa (se borran a las N horas)</option>
                  </select>
                </Campo>
                {formProg.politica === 'gfs' ? (
                  <>
                    <Campo label="Diarios a conservar">
                      <input className={fieldCls} type="number" min="1" value={formProg.ret_diarios}
                             onChange={(e) => setFormProg({ ...formProg, ret_diarios: e.target.value })} />
                    </Campo>
                    <Campo label="Semanales">
                      <input className={fieldCls} type="number" min="0" value={formProg.ret_semanales}
                             onChange={(e) => setFormProg({ ...formProg, ret_semanales: e.target.value })} />
                    </Campo>
                    <Campo label="Mensuales">
                      <input className={fieldCls} type="number" min="0" value={formProg.ret_mensuales}
                             onChange={(e) => setFormProg({ ...formProg, ret_mensuales: e.target.value })} />
                    </Campo>
                  </>
                ) : (
                  <Campo label="Horas a conservar">
                    <input className={fieldCls} type="number" min="1" value={formProg.ret_horas}
                           onChange={(e) => setFormProg({ ...formProg, ret_horas: e.target.value })} />
                  </Campo>
                )}
              </div>
              {formProg.modo === 'incremental' && (
                <p className="text-xs text-amber-600 dark:text-amber-400">
                  El modo incremental (pg_basebackup de PG17) requiere activar summarize_wal en el servidor — fase 2.
                  Hoy el worker lo rechazará con un mensaje claro; usa Full.
                </p>
              )}
              <div className="flex items-center justify-between">
                <Switch checked={formProg.activo} onChange={(v) => setFormProg({ ...formProg, activo: v })} label="Activa" />
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setFormProg(null)}>Cancelar</Button>
                  <Button size="sm" isLoading={guardarProg.isPending} disabled={!formProg.nombre.trim() || !formProg.horario.trim()}
                          onClick={() => guardarProg.mutate({ _data: formProg, _id: progId }, { onSuccess: () => setFormProg(null) })}>
                    Guardar
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Historial de respaldos</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground border-b">
                  <th className="py-2 pr-3 text-right w-10">N°</th>
                  <SortTh col="inicio" sort={sort}>Inicio</SortTh>
                  <SortTh col="nombre" sort={sort}>Programación</SortTh>
                  <SortTh col="estado" sort={sort} align="center">Estado</SortTh>
                  <th className="py-2 pr-3 text-left">Archivo</th>
                  <SortTh col="tamano" sort={sort} align="right">Tamaño</SortTh>
                  <th className="py-2 pr-3 text-right">Duración</th>
                  <SortTh col="verificacion" sort={sort} align="center">Verificación</SortTh>
                  <th className="py-2 pr-3 text-left">Repositorios</th>
                </tr>
              </thead>
              <tbody>
                {sort.sorted.map((e, i) => (
                  <tr key={e.id} className="border-b last:border-0 align-top">
                    <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3 whitespace-nowrap">{new Date(e.inicio).toLocaleString(LOCALE, { hour12: false })}</td>
                    <td className="py-2 pr-3">{e.nombre ?? 'manual'} <span className="text-xs text-muted-foreground uppercase">{e.modo}</span></td>
                    <td className="py-2 pr-3 text-center">
                      {e.estado === 'ok' && <CheckCircle2 className="w-4 h-4 inline text-green-600" />}
                      {e.estado === 'error' && <XCircle className="w-4 h-4 inline text-red-500" />}
                      {(e.estado === 'pendiente' || e.estado === 'en_proceso') && (
                        <span className="text-xs text-amber-600 dark:text-amber-400">{e.estado === 'pendiente' ? 'pendiente' : 'en proceso…'}</span>
                      )}
                      {e.error && <div className="text-xs text-red-600 dark:text-red-400 max-w-[220px]">{e.error}</div>}
                    </td>
                    <td className="py-2 pr-3 font-mono text-xs">{e.archivo ?? '—'}
                      {e.sha256 && <div className="text-muted-foreground">sha256 {e.sha256.slice(0, 12)}…</div>}
                    </td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{mb(e.tamano_bytes)}</td>
                    <td className="py-2 pr-3 text-right">{durSeg(e)}</td>
                    <td className="py-2 pr-3 text-center">
                      {e.verificacion === 'ok' ? <CheckCircle2 className="w-4 h-4 inline text-green-600" />
                        : e.verificacion === 'error' ? <XCircle className="w-4 h-4 inline text-red-500" /> : '—'}
                    </td>
                    <td className="py-2 pr-3 text-xs">
                      {(e.repos ?? []).map((r) => (
                        <div key={r.repo} className={r.estado === 'ok' ? '' : 'text-red-600 dark:text-red-400'}>
                          {r.estado === 'ok' ? '✓' : '✗'} {r.repo}{r.purgados ? ` (purgó ${r.purgados})` : ''}{r.error ? ` — ${r.error}` : ''}
                        </div>
                      ))}
                    </td>
                  </tr>
                ))}
                {!sort.sorted.length && (
                  <tr><td colSpan={9} className="py-6 text-center text-muted-foreground">
                    Aún no hay respaldos. Usa «Respaldar ahora» para el primero.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="text-xs text-muted-foreground border-t pt-2 mt-2 space-y-1">
            <p>Restaurar: <span className="font-mono">pg_restore -d erp --clean --if-exists archivo.dump</span> (o una tabla/esquema con <span className="font-mono">-t</span>/<span className="font-mono">-n</span>).</p>
            <p>El dump incluye la base «erp» completa (todos los esquemas). NO incluye roles ni configuración del servidor: tras montar un servidor nuevo, aplica los scripts 01→NN o un <span className="font-mono">pg_dumpall --globals-only</span> ocasional.</p>
            <p>Un respaldo no probado no es un respaldo: restaura de prueba en una BD temporal (p.ej. <span className="font-mono">erp_verify</span>) al menos una vez al mes.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
